import moment from "moment";
import dayjs from "dayjs";

import { EmptyString } from "constants/strings";
import { UNUSED_FUNDS_START_NOTIFICATION_PERIOD } from "./envConstants";

moment.locale("ru");

export function dateFormat(date: number | Date | string, isFull?: boolean): string {
  if (!date) {
    return EmptyString;
  }
  return dayjs(date).format(isFull ? "DD.MM.YYYY, HH:mm" : "DD.MM.YYYY");
}

export function dateISOFormat(date: any, isFull: boolean = true): string {
  if (!date) return "";
  if (dayjs.isDayjs(date)) {
    return isFull ? date.toISOString() : date.format("YYYY-MM-DD");
  }
  if (typeof date === "string") {
    return date;
  }
  return "";
}

export function timeFormat(date: number | Date | string): string {
  return dayjs(date).format("HH:mm");
}

export const isDefined = (value: any) => value !== null && value !== undefined;

export const getTimeDifferenceInMs = (utcDate: Date | string) => {
  return Date.now() - new Date(utcDate).getTime();
};

export const getExpirationTime = (hours: number, minutes: number): string => {
  if (Number(hours) || Number(minutes)) {
    return dayjs().add(hours, "hour").add(minutes, "minute").toISOString();
  } else return "";
};

export function customDateFormat(date: number | Date | string, tmpl = "DD.MM.YYYY HH:mm"): string {
  if (!date) {
    return EmptyString;
  }
  return moment(date).format(tmpl);
}

export function isEqualDates(initDt: string | Date, dt: string | Date) {
  if (!moment.isDate(initDt) && moment.isDate(dt)) {
    return false;
  }

  if (moment.isDate(initDt) && !moment.isDate(dt)) {
    return false;
  }

  if (moment.isDate(initDt) && moment.isDate(dt)) {
    return initDt.getTime() - dt.getTime() === 0;
  }

  return initDt === dt;
}

export const combineDateAndTime = (date: Date, time: string) => {
  const [hours, mins] = time.trim().split(":");
  const combined = moment(date).set({
    hours: Number(hours),
    minutes: Number(mins),
  });

  return combined;
};

export const convertToISODuration = (hours: number, minutes: number): string => {
  if (!isDefined(hours) || !isDefined(minutes)) return "";

  return `PT${!!hours ? hours : 0}H${!!minutes ? minutes : 0}M`;
};

export const convertFromISODuration = (iso: string): any[] => {
  const str = typeof iso === "string" ? iso : "";
  const daysMatch = str.match(/(\d+)D/);
  const hoursMatch = str.match(/(\d+)H/);
  const minutesMatch = str.match(/(\d+)M(?!.*D)/);
  const secondsMatch = str.match(/(\d+)S/);

  const days = daysMatch ? daysMatch[1] : 0;
  const hours = hoursMatch ? hoursMatch[1] : 0;
  const minutes = minutesMatch ? minutesMatch[1] : 0;
  const seconds = secondsMatch ? secondsMatch[1] : 0;

  return [days, hours, minutes, seconds];
};

export const convertFromISOHourMinuteDuration = (iso: string): [number, number] => {
  const parts = convertFromISODuration(iso);
  const days = parts[0] ? parseInt(parts[0]) : 0;
  const hours = (parts[1] ? parseInt(parts[1]) : 0) + days * 24;
  const minutes = parts[2] ? parseInt(parts[2]) : 0;

  return [hours, minutes];
};

export const isValidDateRange = (dateRange: dayjs.Dayjs[]) => {
  if (dateRange && Array.isArray(dateRange) && dateRange[0] && dateRange[1]) {
    return true;
  }
  return false;
};

export const validateExpirationTime = (hours: string, minutes: string): boolean => {
  if (!hours && !minutes) {
    return false;
  }

  const hoursNum = Number(hours);
  const minutesNum = Number(minutes);

  if ((hoursNum && isNaN(hoursNum)) || (minutesNum && isNaN(minutesNum))) {
    return false;
  }

  if (hours && (hoursNum < 0 || !Number.isInteger(hoursNum))) {
    return false;
  }

  if (minutes && (minutesNum < 0 || minutesNum >= 60 || !Number.isInteger(minutesNum))) {
    return false;
  }

  return true;
};

export const convertFromSecondsToTime = (seconds: number, withHours?: boolean) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  const padZero = (num: number) => (num < 10 ? `0${num}` : `${num}`);

  return withHours
    ? `${padZero(hours)}:${padZero(minutes)}:${padZero(remainingSeconds)}`
    : `${padZero(minutes)}:${padZero(remainingSeconds)}`;
};

/**
 * Возвращает, сколько дней прошло с даты,
 * и проверяет, прошло ли больше заданного количества дней.
 *
 * @param date - Дата в формате Date или ISO-строки, с которой начинается отсчёт.
 * @param overDays - Необязательный параметр. Если указан, функция проверяет,
 * прошло ли указанное количество дней с даты. Если не указан, возвращается null.
 *
 * @returns Объект с двумя свойствами:
 *  - days: количество полных дней, прошедших с даты до текущего дня.
 *  - isOverDays: true, если прошло ≥ overDays дней, false, если ещё не прошло,
 * или null, если параметр overDays не передан.
 *
 * @example
 * checkDaysPassed("2025-01-01T00:00:00.000Z", 85)
 * // { days: 236, isOverDays: true } на 25.08.2025
 */
export const checkDaysPassed = (date: Date | string, overDays?: number) => {
  const days = dayjs().diff(dayjs(date), "day");

  const isOverDays = overDays !== undefined ? days >= overDays : null;

  return { days, isOverDays };
};

export const notificationPeriodDays = convertFromISODuration(
  String(UNUSED_FUNDS_START_NOTIFICATION_PERIOD).toUpperCase()
)[0];
